<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanApi from 'dashboard/api/kanban';
import ConversationFunnelProducts from './ConversationFunnelProducts.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const loading = ref(false);
const showAddModal = ref(false);
const pipelines = ref([]);
const selectedPipeline = ref(null);
const showConfirmModal = ref(false);
const confirmAction = ref(null);
const confirmData = ref(null);
const selectedColumn = ref(null);
const modalStep = ref(1); // 1 = escolher funil, 2 = escolher coluna

// Funis da conversa
const conversationFunnels = computed(
  () =>
    getters['conversationFunnels/getConversationFunnels'].value(
      props.conversationId
    ) || []
);

const uiFlags = computed(() => getters['conversationFunnels/getUIFlags'].value);

const selectPipeline = pipeline => {
  selectedPipeline.value = pipeline;
  selectedColumn.value = pipeline.kanban_columns?.[0] || null;
  modalStep.value = 2;
};

const addToFunnel = async () => {
  if (!selectedColumn.value) {
    console.error('Nenhuma coluna selecionada');
    return;
  }

  try {
    loading.value = true;

    // Salvar o ID da coluna antes de fechar o modal
    const kanbanColumnId = selectedColumn.value.id;

    // Fechar o modal imediatamente para não travar a UI
    closeModal();

    // Executar a criação do funil em background
    await store.dispatch('conversationFunnels/create', {
      conversationId: props.conversationId,
      kanbanColumnId: kanbanColumnId,
    });
  } catch (error) {
    console.error('Erro ao adicionar ao funil:', error);
    // TODO: Mostrar notificação de erro
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  showAddModal.value = false;
  selectedPipeline.value = null;
  selectedColumn.value = null;
  modalStep.value = 1;
};

const confirmRemoveFunnel = funnel => {
  confirmAction.value = 'removeFunnel';
  confirmData.value = funnel;
  showConfirmModal.value = true;
};

const confirmRemoveProduct = product => {
  confirmAction.value = 'removeProduct';
  confirmData.value = product;
  showConfirmModal.value = true;
};

const executeConfirmAction = async () => {
  try {
    loading.value = true;

    if (confirmAction.value === 'removeFunnel') {
      await store.dispatch('conversationFunnels/delete', {
        conversationId: props.conversationId,
        funnelId: confirmData.value.id,
      });
    } else if (confirmAction.value === 'removeProduct') {
      // Esta função será chamada pelo componente ConversationFunnelProducts
      if (confirmData.value.onConfirm) {
        await confirmData.value.onConfirm();
      }
    }

    closeConfirmModal();
  } catch (error) {
    console.error('Erro ao executar ação:', error);
  } finally {
    loading.value = false;
  }
};

const closeConfirmModal = () => {
  showConfirmModal.value = false;
  confirmAction.value = null;
  confirmData.value = null;
};

const removeFromFunnel = async funnelId => {
  try {
    loading.value = true;
    await store.dispatch('conversationFunnels/delete', {
      conversationId: props.conversationId,
      funnelId,
    });
  } catch (error) {
    console.error('Erro ao remover do funil:', error);
    // TODO: Mostrar notificação de erro
  } finally {
    loading.value = false;
  }
};

const moveInFunnel = async (funnel, newColumnId) => {
  try {
    loading.value = true;
    await store.dispatch('conversationFunnels/update', {
      conversationId: props.conversationId,
      funnelId: funnel.id,
      kanbanColumnId: newColumnId,
    });
  } catch (error) {
    console.error('Erro ao mover no funil:', error);
    // TODO: Mostrar notificação de erro
  } finally {
    loading.value = false;
  }
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const loadPipelines = async () => {
  try {
    const response = await KanbanApi.getPipelines();
    pipelines.value = response.data || [];
  } catch (error) {
    console.error('Erro ao carregar pipelines:', error);
    pipelines.value = [];
  }
};

// Computed para obter a conversa atual
const currentConversation = computed(() => {
  try {
    // Tentar diferentes formas de acessar a conversa
    const allConversations = store.state.conversations?.allConversations || [];
    const conversation = allConversations.find(
      c => c.id === Number(props.conversationId)
    );

    return conversation || null;
  } catch (error) {
    console.error('Erro ao buscar conversa:', error);
    return null;
  }
});

const getKanbanCardId = funnel => {
  // Se o funil tem um kanban_card_id direto, usar ele
  if (funnel.kanban_card_id) {
    return funnel.kanban_card_id;
  }

  // Buscar o kanban card correspondente a este funil
  const conversation = currentConversation.value;

  if (!conversation?.kanban_cards) {
    return null;
  }

  // Buscar qualquer card associado à conversa (independente da coluna)
  // O card pode ter sido movido para outra coluna
  const kanbanCard = conversation.kanban_cards.find(card => {
    // Buscar por pipeline, não por coluna, já que o card pode ter sido movido
    return card.kanban_pipeline_id === funnel.kanban_pipeline.id;
  });

  if (kanbanCard?.id) {
    return kanbanCard.id;
  }

  return null;
};

// Computed para mapear funis com seus respectivos kanban card IDs
const funnelsWithCardIds = computed(() => {
  // Forçar reatividade às mudanças na conversa
  const conversation = currentConversation.value;
  const kanbanCards = conversation?.kanban_cards || [];

  // SEMPRE retornar os funis, mesmo se não há cards ainda
  // Os cards podem estar sendo carregados em background
  return conversationFunnels.value.map(funnel => {
    const kanbanCardId = getKanbanCardId(funnel);
    return {
      ...funnel,
      kanbanCardId,
    };
  });
});

// Carregar dados quando o componente for montado
onMounted(async () => {
  // Carregar pipelines disponíveis
  await loadPipelines();
  // Carregar funis da conversa
  await store.dispatch('conversationFunnels/get', props.conversationId);
  // Forçar recarregamento da conversa para incluir kanban_cards
  await reloadConversation();

  // Listener para eventos de mudança de funil
  const handleFunnelUpdate = event => {
    const { conversationId, action } = event.detail;

    // Se é a mesma conversa, processar diferentes tipos de ações
    if (conversationId === props.conversationId) {
      if (action === 'create') {
        // Novo funil criado
        setTimeout(async () => {
          await reloadConversation();
          await store.dispatch('conversationFunnels/get', props.conversationId);
        }, 500);
      } else if (action === 'move') {
        // Card movido entre colunas
        setTimeout(async () => {
          await reloadConversation();
          await store.dispatch('conversationFunnels/get', props.conversationId);
        }, 300);
      } else if (action === 'update') {
        // Funil atualizado
        setTimeout(async () => {
          await store.dispatch('conversationFunnels/get', props.conversationId);
        }, 200);
      }
    }
  };

  window.addEventListener('funnelUpdated', handleFunnelUpdate);

  // Listener para eventos de mudança de produtos
  const handleProductsUpdate = event => {
    const { conversationId, action } = event.detail;

    // Produtos são gerenciados pelo componente filho ConversationFunnelProducts
    // Não precisamos recarregar os funis aqui, apenas log para debug
    if (conversationId === props.conversationId) {
      console.log(
        '📦 Produtos atualizados para conversa',
        conversationId,
        '- ação:',
        action
      );
      // Os produtos serão atualizados pelo componente filho via seus próprios listeners WebSocket
    }
  };

  // Listener para WebSocket de movimentação do kanban
  const handleKanbanConversationMoved = event => {
    const { conversationId, toColumnId, pipelineId } = event.detail;
    console.log(
      '🔔 WebSocket: Kanban conversation moved in ConversationFunnels',
      { conversationId, toColumnId, pipelineId }
    );

    // Se é a mesma conversa, atualizar apenas a coluna do funil específico
    if (Number(conversationId) === Number(props.conversationId)) {
      console.log(
        '🔄 Atualizando coluna do funil após movimentação no Kanban...'
      );

      // Buscar o funil correspondente ao pipelines
      const currentFunnels = conversationFunnels.value;
      const funnelToUpdate = currentFunnels.find(
        f => f.kanban_pipeline.id === pipelineId
      );

      if (funnelToUpdate) {
        // Atualizar apenas o kanban_column_id do funil sem recarregar tudo
        console.log(
          '📍 Atualizando funil',
          funnelToUpdate.id,
          'para coluna',
          toColumnId
        );

        // Fazer update otimista localmente (vai ser atualizado pelo Vuex também)
        // Mas isso evita recarregar e perder o estado do dropdown
        funnelToUpdate.kanban_column_id = toColumnId;

        // Buscar a coluna completa para atualizar o nome também
        const pipeline = pipelines.value.find(p => p.id === pipelineId);
        if (pipeline) {
          const newColumn = pipeline.kanban_columns?.find(
            col => col.id === toColumnId
          );
          if (newColumn) {
            funnelToUpdate.kanban_column = newColumn;
          }
        }
      }
    }
  };

  window.addEventListener('productsUpdated', handleProductsUpdate);
  window.addEventListener(
    'kanbanConversationMoved',
    handleKanbanConversationMoved
  );

  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('funnelUpdated', handleFunnelUpdate);
    window.removeEventListener('productsUpdated', handleProductsUpdate);
    window.removeEventListener(
      'kanbanConversationMoved',
      handleKanbanConversationMoved
    );
  });
});

// Watcher para detectar mudanças nos funis e forçar reatividade
watch(
  () => conversationFunnels.value,
  (newFunnels, oldFunnels) => {
    // Se novos funis foram adicionados, forçar atualização da conversa
    if (newFunnels.length > (oldFunnels?.length || 0)) {
      setTimeout(() => {
        reloadConversation();
      }, 100);
    }
  },
  { deep: true }
);

const reloadConversation = async () => {
  try {
    await store.dispatch('getConversation', props.conversationId);
  } catch (error) {
    console.error('Erro ao recarregar conversa:', error);
  }
};
</script>

<template>
  <div class="conversation-funnels" style="margin-top: 20px">
    <!-- Header -->
    <div class="p-4 bg-n-solid-1 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <div class="text-sm text-n-slate-11">
          {{ t('CONVERSATION_FUNNELS.DESCRIPTION') }}
        </div>
        <Button
          faded
          xs
          icon="i-lucide-plus"
          class="mt-1"
          @click="showAddModal = true"
        >
          {{ t('CONVERSATION_FUNNELS.ADD') }}
        </Button>
      </div>
    </div>

    <!-- Funnels list -->
    <div class="p-4 space-y-3" style="background-color: #17171a">
      <div
        v-if="!conversationFunnels.length"
        class="text-center py-8 text-n-slate-11"
      >
        <i class="i-lucide-layout-grid size-8 mx-auto mb-2 opacity-50" />
        <p class="text-sm">{{ t('CONVERSATION_FUNNELS.EMPTY_STATE') }}</p>
      </div>

      <div
        v-for="funnel in funnelsWithCardIds"
        :key="funnel.id"
        class="border border-n-weak rounded-lg bg-n-background"
      >
        <div class="p-3">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <i class="i-lucide-layout-grid size-4 text-n-slate-9" />
              <span class="font-medium text-n-slate-12">{{
                funnel.kanban_pipeline.name
              }}</span>
            </div>
            <button
              class="w-8 h-8 rounded-md flex items-center justify-center text-red-600 hover:bg-red-50 hover:text-red-700 border border-red-200 hover:border-red-300 transition-all duration-200"
              :disabled="loading"
              :title="t('CONVERSATION_FUNNELS.REMOVE_FROM_FUNNEL')"
              @click="confirmRemoveFunnel(funnel)"
            >
              <i class="i-lucide-trash-2 size-5" />
            </button>
          </div>

          <div class="flex items-center justify-between">
            <span class="text-xs text-n-slate-11">{{ t('CONVERSATION_FUNNELS.CURRENT_STAGE') }}:</span>

            <select
              :value="funnel.kanban_column.id"
              class="text-xs bg-n-background border border-n-weak rounded px-2 py-1 text-n-slate-12 hover:border-n-slate-9 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="loading"
              @change="moveInFunnel(funnel, parseInt($event.target.value))"
            >
              <option
                v-for="column in funnel.kanban_pipeline.kanban_columns"
                :key="column.id"
                :value="column.id"
                class="bg-n-background text-n-slate-12 py-1"
              >
                {{ column.name }}
              </option>
            </select>
          </div>

          <!-- Products Section -->
          <div class="mt-3 pt-3 border-t border-n-weak">
            <ConversationFunnelProducts
              :conversation-id="conversationId"
              :funnel-id="funnel.kanban_pipeline.id"
              :kanban-card-id="funnel.kanbanCardId"
              :on-confirm-remove="confirmRemoveProduct"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Add to Funnel Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      style="
        background-color: rgba(var(--black-alpha-1));
        --tw-backdrop-blur: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        backdrop-filter: blur(4px);
      "
      @click.self="closeModal"
    >
      <div class="bg-n-background rounded-lg shadow-lg w-full max-w-md m-4">
        <!-- Step 1: Select Pipeline -->
        <div v-if="modalStep === 1">
          <div class="p-4 border-b border-n-weak">
            <h3 class="font-medium text-n-slate-12">
              {{ t('CONVERSATION_FUNNELS.SELECT_FUNNEL') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              Escolha o funil para adicionar esta conversa
            </p>
          </div>

          <div class="max-h-80 overflow-y-auto">
            <div
              v-if="!pipelines.length"
              class="p-4 text-center text-n-slate-11"
            >
              {{ t('CONVERSATION_FUNNELS.NO_FUNNELS') }}
            </div>

            <button
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :disabled="
                conversationFunnels.some(
                  f => f.kanban_pipeline.id === pipeline.id
                ) || loading
              "
              class="w-full p-3 text-left hover:bg-n-solid-1 border-b border-n-weak last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="selectPipeline(pipeline)"
            >
              <div class="flex items-center gap-3">
                <i class="i-lucide-layout-grid size-4 text-n-slate-9" />
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-n-slate-12 truncate">
                    {{ pipeline.name }}
                  </div>
                  <div class="text-xs text-n-slate-11 truncate">
                    {{ pipeline.description }}
                  </div>
                </div>
                <div
                  v-if="
                    conversationFunnels.some(
                      f => f.kanban_pipeline.id === pipeline.id
                    )
                  "
                  class="text-green-600"
                >
                  <i class="i-lucide-check size-4" />
                </div>
                <i class="i-lucide-chevron-right size-4 text-n-slate-9" />
              </div>
            </button>
          </div>
        </div>

        <!-- Step 2: Select Column -->
        <div v-else-if="modalStep === 2 && selectedPipeline">
          <div class="p-4 border-b border-n-weak">
            <div class="flex items-center gap-2 mb-2">
              <button
                class="text-n-slate-9 hover:text-n-slate-12"
                @click="modalStep = 1"
              >
                <i class="i-lucide-chevron-left size-4" />
              </button>
              <h3 class="font-medium text-n-slate-12">Escolher Coluna</h3>
            </div>
            <p class="text-sm text-n-slate-11">{{ selectedPipeline.name }}</p>
          </div>

          <div class="max-h-80 overflow-y-auto">
            <div
              v-if="!selectedPipeline.kanban_columns?.length"
              class="p-4 text-center text-n-slate-11"
            >
              Este funil não tem colunas configuradas
            </div>

            <button
              v-for="column in selectedPipeline.kanban_columns"
              :key="column.id"
              class="w-full p-3 text-left hover:bg-n-solid-1 border-b border-n-weak last:border-b-0"
              :class="{ 'bg-n-solid-1': selectedColumn?.id === column.id }"
              @click="selectedColumn = column"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-4 h-4 rounded-full"
                  :style="{ backgroundColor: column.color }"
                />
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-n-slate-12 truncate">
                    {{ column.name }}
                  </div>
                  <div class="text-xs text-n-slate-11 truncate">
                    {{ column.description }}
                  </div>
                </div>
                <div
                  v-if="selectedColumn?.id === column.id"
                  class="text-blue-600"
                >
                  <i class="i-lucide-check size-4" />
                </div>
              </div>
            </button>
          </div>

          <div class="flex gap-3 p-4 border-t border-n-weak">
            <Button slate class="flex-1" @click="closeModal"> Cancelar </Button>
            <Button
              class="flex-1"
              :disabled="!selectedColumn || loading"
              :loading="loading"
              @click="addToFunnel"
            >
              Adicionar
            </Button>
          </div>
        </div>

        <!-- Fallback for step 1 -->
        <div v-else class="p-4 border-t border-n-weak">
          <Button slate class="w-full" @click="closeModal"> Cancelar </Button>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal -->
  <div
    v-if="showConfirmModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    style="
      background-color: rgba(var(--black-alpha-1));
      --tw-backdrop-blur: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      backdrop-filter: blur(4px);
    "
    @click.self="closeConfirmModal"
  >
    <div
      class="bg-white dark:bg-n-slate-1 rounded-lg shadow-xl max-w-md w-full mx-4"
    >
      <div class="p-6">
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center"
          >
            <i
              class="i-lucide-alert-triangle size-5 text-red-600 dark:text-red-400"
            />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                confirmAction === 'removeFunnel'
                  ? 'Remover Funil'
                  : 'Remover Produto'
              }}
            </h3>
            <p class="text-sm text-n-slate-11">
              Esta ação não pode ser desfeita
            </p>
          </div>
        </div>

        <p class="text-n-slate-11 mb-6">
          <span v-if="confirmAction === 'removeFunnel'">
            Tem certeza que deseja remover esta conversa do funil
            <strong>"{{ confirmData?.kanban_pipeline?.name }}"</strong>? Todos
            os produtos associados a este funil serão perdidos.
          </span>
          <span v-else-if="confirmAction === 'removeProduct'">
            Tem certeza que deseja remover o produto
            <strong>"{{ confirmData?.product?.name }}"</strong>?
          </span>
        </p>

        <div class="flex gap-3 justify-end">
          <Button slate :disabled="loading" @click="closeConfirmModal">
            Cancelar
          </Button>
          <Button danger :loading="loading" @click="executeConfirmAction">
            {{
              confirmAction === 'removeFunnel'
                ? 'Remover Funil'
                : 'Remover Produto'
            }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
